import { asClipId, asProjectId, asSourceId, asTrackId, type IdFactory } from "@/core/model/ids.ts";
import {
  addClip,
  addSource,
  addTrack,
  createProject,
  type Clip,
  type Project,
  type Track,
} from "@/core/model/project.ts";
import { sourceDurationSec, type AudioSource, type Source } from "@/core/model/source.ts";
import { originalPath, posterPath } from "@/core/storage/paths.ts";
import type { BlobStore } from "@/core/storage/opfs.ts";
import { decodeAudioFile, detachChannels, WORKING_SAMPLE_RATE } from "./audioFormat.ts";
import { ingestAudio, type IngestStep } from "./ingestAudio.ts";
import { baseName } from "./kind.ts";
import { renderPoster, renderVideoPoster, renderWaveformPoster } from "./poster.ts";
import { assertSupported, probeImage, probeVideo } from "./probe.ts";

/**
 * Import is several seconds of work for a long recording, so each stage is announced.
 * Without it the app looks hung while it decodes and writes tens of megabytes.
 */
export type ImportStage = "reading" | "decoding" | IngestStep;

export const IMPORT_STAGE_LABELS: { readonly [K in ImportStage]: string } = {
  reading: "ファイルを読んでいます",
  decoding: "音声を展開しています",
  interleaving: "音声を並べ替えています",
  analyzing: "波形を作っています",
  writing: "保存しています",
};

export type ImportDeps = {
  readonly blobs: BlobStore;
  readonly newId: IdFactory;
  readonly now: () => string;
  readonly onStage?: (stage: ImportStage) => void;
};

type Identity = { id: ReturnType<typeof asSourceId>; name: string; importedAt: string };

function audioSourceOf(file: File, identity: Identity, buffer: AudioBuffer): AudioSource {
  return {
    kind: "audio",
    id: identity.id,
    name: identity.name,
    mimeType: file.type,
    sizeBytes: file.size,
    importedAt: identity.importedAt,
    durationSec: buffer.duration,
    // The rate of the PCM this app stores, not of the file that was dropped in.
    sampleRateHz: WORKING_SAMPLE_RATE,
    channelCount: buffer.numberOfChannels,
  };
}

/** Builds a one-clip project around a dropped file and persists the original bytes. */
export async function createProjectFromFile(file: File, deps: ImportDeps): Promise<Project> {
  const projectId = asProjectId(deps.newId());
  const sourceId = asSourceId(deps.newId());
  const now = deps.now();

  const identity = { id: sourceId, name: file.name, importedAt: now };
  const kind = assertSupported(file);

  deps.onStage?.("reading");
  await deps.blobs.write(originalPath(projectId, sourceId), file);

  let source: Source;
  if (kind === "image") {
    source = await probeImage(file, identity);
    await writePoster(file, projectId, identity.id, deps);
  } else if (kind === "video") {
    source = await probeVideo(file, identity);
    await writeVideoPoster(file, projectId, identity.id, deps);
  } else {
    source = await importAudio(file, identity, projectId, deps);
  }

  const track: Track = {
    id: asTrackId(deps.newId()),
    kind: source.kind,
    muted: false,
    clips: [],
  };
  const clip: Clip = {
    id: asClipId(deps.newId()),
    sourceId,
    inSec: 0,
    outSec: sourceDurationSec(source),
    atSec: 0,
    ops: [],
  };

  const base = createProject({
    id: projectId,
    name: baseName(file.name),
    kind: source.kind,
    now,
  });
  return addClip(addTrack(addSource(base, source), track), track.id, clip);
}

/**
 * Decodes once and caches the PCM and the waveform pyramid, so opening the editor later
 * never decodes again. Everything after the decode happens in a worker, because on a long
 * recording those steps are seconds of work that would otherwise freeze the window.
 */
async function importAudio(
  file: File,
  identity: Identity,
  projectId: string,
  deps: ImportDeps,
): Promise<AudioSource> {
  deps.onStage?.("decoding");
  const buffer = await decodeAudioFile(file);
  const source = audioSourceOf(file, identity, buffer);
  const { channels, frameCount } = detachChannels(buffer);

  // Before the ingest: it transfers the buffers, and this reads them.
  await writeWaveformPoster(channels, frameCount, projectId, identity.id, deps);

  await ingestAudio({ projectId, sourceId: identity.id, channels, frameCount }, (step) =>
    deps.onStage?.(step),
  );
  return source;
}

async function writeWaveformPoster(
  channels: readonly Float32Array[],
  frameCount: number,
  projectId: string,
  sourceId: string,
  deps: ImportDeps,
): Promise<void> {
  const poster = await renderWaveformPoster(channels, frameCount);
  if (poster) await deps.blobs.write(posterPath(projectId, sourceId), poster);
}

async function writeVideoPoster(
  file: File,
  projectId: string,
  sourceId: string,
  deps: ImportDeps,
): Promise<void> {
  const poster = await renderVideoPoster(file);
  if (poster) await deps.blobs.write(posterPath(projectId, sourceId), poster);
}

async function writePoster(
  file: File,
  projectId: string,
  sourceId: string,
  deps: ImportDeps,
): Promise<void> {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return;
  try {
    const poster = await renderPoster(bitmap);
    if (poster) await deps.blobs.write(posterPath(projectId, sourceId), poster);
  } finally {
    bitmap.close();
  }
}
